/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useState, useEffect, useCallback } from "react";

import { useSelector } from "react-redux";
import { RootState } from "store/types";

import { OrgSelectorProps } from ".";

export default function useOrgSelector({
  currentOrgID,
  onChange,
  optionList,
}: OrgSelectorProps) {
  const _getSelectedOrgName = () => {
    // return the corresponding organization name given the current org ID
    const selectedOrg = currentOrgID
      ? optionList.find((user) => user.id === currentOrgID)
      : null;
    return selectedOrg?.organization?.name || "Unnamed Organization";
  };

  const selfObject = useSelector((state: RootState) => state.selfObject);
  const selectedSummit = useSelector(
    (state: RootState) => state.selectedSummit
  );
  const [isHovering, setIsHovering] = useState(false);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [text, setText] = useState(_getSelectedOrgName);

  const reloadRouteStrings = ["dashboard", "onboarding"];
  const childRouteStrings = ["detail", "module"];

  useEffect(() => {
    setText(_getSelectedOrgName);
  }, [currentOrgID, optionList]);

  useEffect(() => {
    // If a logged-in user is accessing a summit,
    // check to see if the user has a membership to that summit's organization;
    // if so, switch to that organization
    if (
      selectedSummit?.organization &&
      selfObject.organizationCode !== selectedSummit?.organization?.orgCode
    ) {
      const summitMember = optionList.find(
        (member) =>
          member?.organization?.orgCode ===
          selectedSummit?.organization?.orgCode
      );
      if (summitMember) {
        _handleChange(summitMember.id!, summitMember?.organization?.name!);
      }
    }
  }, [selfObject.organizationCode, selectedSummit, optionList]);

  const _handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const _handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const _handleChange = useCallback(
    (userId: string, text: string) => {
      setText(text);
      _handleClose();
      onChange(userId, () => {
        if (
          reloadRouteStrings.some((routeString) =>
            window.location.href.includes(routeString)
          )
        ) {
          window.location.reload();
        } else {
          for (let k = 0; k < childRouteStrings.length; k++) {
            if (window.location.href.includes(childRouteStrings[k])) {
              const splitUrl = window.location.href.split(childRouteStrings[k]);
              window.location.replace(splitUrl[0]);
              break;
            }
          }
        }
      });
    },
    [onChange, selfObject.userId, _handleClose]
  );

  return {
    isHovering,
    anchorEl,
    text,
    setIsHovering,
    setAnchorEl,
    setText,
    _handleClick,
    _handleChange,
    _handleClose,
  };
}
